import axios, { AxiosError, type InternalAxiosRequestConfig } from 'axios'
import { useAuthStore } from '@/stores/auth-store'
import { useUiStore } from '@/stores/ui-store'
import type { ApiProblem, AuthTokens } from '@/types/api'

const baseURL = import.meta.env.VITE_API_URL || '/api/v1'

export const api = axios.create({ baseURL, timeout: 30_000, headers: { 'Content-Type': 'application/json' } })

api.interceptors.request.use((config) => {
  const { accessToken } = useAuthStore.getState()
  config.headers.set('Accept-Language', useUiStore.getState().locale)
  if (accessToken) config.headers.set('Authorization', `Bearer ${accessToken}`)
  return config
})

interface RetryConfig extends InternalAxiosRequestConfig { _retry?: boolean }
let refreshPromise: Promise<AuthTokens> | null = null

api.interceptors.response.use(undefined, async (error: AxiosError<ApiProblem>) => {
  const config = error.config as RetryConfig | undefined
  const state = useAuthStore.getState()
  const isAuthRoute = config?.url?.startsWith('/auth/')
  if (error.response?.status !== 401 || !config || config._retry || isAuthRoute || !state.refreshToken) return Promise.reject(error)

  config._retry = true
  refreshPromise ??= axios.post<AuthTokens>(`${baseURL}/auth/refresh`, {
    refreshToken: state.refreshToken,
    deviceName: navigator.userAgent.slice(0, 120),
  }, { headers: { 'Accept-Language': useUiStore.getState().locale } }).then(({ data }) => {
    useAuthStore.getState().setSession(data)
    return data
  }).finally(() => { refreshPromise = null })

  try {
    const tokens = await refreshPromise
    config.headers.set('Authorization', `Bearer ${tokens.accessToken}`)
    return api(config)
  } catch {
    useAuthStore.getState().clearSession()
    window.location.assign('/login')
    return Promise.reject(error)
  }
})

export function problemMessage(error: unknown, fallback: string) {
  if (axios.isAxiosError<ApiProblem>(error)) return error.response?.data?.title ?? fallback
  return fallback
}
